import find from 'lodash/find';
import { matchPath } from 'react-router-dom';
import { compile, match } from 'path-to-regexp';
import { createSlug, getTranslatedPath, stringify, TRANSLATED_PATHS } from './urlHelpers';
import { detectUserDefaultLanguage } from './data';
import { SUPPORTED_LANGUAGES } from './constants';
// NOTE: This file imports urlHelpers.js, which may lead to circular dependency
const findRouteByName = (nameToFind, routes) => find(routes, route => route.name === nameToFind);
/**
 * E.g. ```const toListingPath = toPathByRouteName('ListingPage', routes);```
 * Then we can generate listing paths with given params (```toListingPath({ id: uuidX })```)
 */
const toPathByRouteName = (nameToFind, routes) => {
  const route = findRouteByName(nameToFind, routes);
  if (!route) {
    throw new Error(`Path "${nameToFind}" was not found.`);
  }
  return compile(route.path);
};
/**
 * Shorthand for single path call. (```pathByRouteName('ListingPage', routes, { id: uuidX });```)
 */
export const pathByRouteName = (nameToFind, routes, params = {}) => {
  const listingRouteFallbackByName = {
    ListingPage: {
      withCityAndCategory: 'ListingPageCityCategory',
      withCity: 'ListingPageCity',
      withCategory: 'ListingPage',
      defaultRoute: 'ListingPage',
    },
  };

  const profileRouteFallbackByName = {
    ProfilePage: 'ProfilePageNoCity',
    ProfilePageAbout: 'ProfilePageNoCityAbout',
    ProfilePageReviews: 'ProfilePageNoCityReviews',
    ProfilePageGallery: 'ProfilePageNoCityGallery',
    ProfilePageFaqs: 'ProfilePageNoCityFaqs',
    ProfilePageBlog: 'ProfilePageNoCityBlog',
  };
  const isProfileRoute = Object.prototype.hasOwnProperty.call(
    profileRouteFallbackByName,
    nameToFind
  );
  const isListingRoute = Object.prototype.hasOwnProperty.call(
    listingRouteFallbackByName,
    nameToFind
  );
  const hasSlug = typeof params?.slug === 'string' && params.slug.trim().length > 0;
  const hasCity = typeof params?.city === 'string' && params.city.trim().length > 0;
  const hasCategory = typeof params?.category === 'string' && params.category.trim().length > 0;
  const resolvedRouteName = isListingRoute
    ? hasCity && hasCategory
      ? listingRouteFallbackByName[nameToFind].withCityAndCategory
      : hasCity
        ? listingRouteFallbackByName[nameToFind].withCity
        : hasCategory
          ? listingRouteFallbackByName[nameToFind].withCategory
          : listingRouteFallbackByName[nameToFind].defaultRoute
    : isProfileRoute
      ? hasSlug
        ? hasCity
          ? nameToFind
          : profileRouteFallbackByName[nameToFind]
        : nameToFind === 'ProfilePage' && params?.id
          ? 'ProfilePageLegacy'
          : nameToFind
      : nameToFind;

  const lang = params.lang || detectUserDefaultLanguage();
  const hasEmptySlug = params.hasOwnProperty('slug') && params.slug === '';
  const pathParams = hasEmptySlug ? { ...params, slug: 'no-slug' } : { ...params };
  const matchingRoute = routes.find(r => r.name === resolvedRouteName);
  if (!matchingRoute) {
    throw new Error(`No matching route found for routeName: ${resolvedRouteName}`);
  }
  const fallbackPath = matchingRoute.fallbackPath || matchingRoute.path;
  const translatedPath = getTranslatedPath(resolvedRouteName, fallbackPath, lang);
  const fullPath = `${lang !== 'en' ? `/${lang}` : ''}${translatedPath}`;
  const compiler = compile(fullPath, { encode: encodeURIComponent });
  return compiler(pathParams);
};
/**
 * Find the matching routes and their params for the given pathname
 *
 * @param {String} pathname - Full URL path from root with possible
 * search params and hash included
 *
 * @return {Array<{ route, params }>} - All matches as { route, params } objects if matches has
 * exact flag set to false. If not, an array containing just the first matched exact route is returned.
 */
export const matchPathname = (pathname, routeConfiguration) => {
  const matchedRoutes = routeConfiguration.reduce((matches, route) => {
    const { path, exact = true } = route;
    const match = matchPath(pathname, { path, exact });
    if (match) {
      matches.push({
        route,
        params: match.params || {},
      });
    }
    return matches;
  }, []);
  const matchedExactRoute = matchedRoutes.find(r => {
    return r.exact === true || r.exact == null;
  });
  // We return matched 'exact' path route only if such exists
  // and all matches if no exact flag exists.
  return matchedExactRoute ? [matchedExactRoute] : matchedRoutes;
};
/**
 * ResourceLocatorString is used to direct webapp to correct page.
 * In contrast to Universal Resource Locator (URL), this doesn't contain protocol, host, or port.
 */
export const createResourceLocatorString = (
  routeName,
  routes,
  pathParams = {},
  searchParams = {},
  hash = ''
) => {
  const searchQuery = stringify(searchParams);
  const includeSearchQuery = searchQuery.length > 0 ? `?${searchQuery}` : '';
  const path = pathByRouteName(routeName, routes, pathParams);
  return `${path}${includeSearchQuery}${hash}`;
};
/**
 * Find component related to route name
 * E.g. `const PageComponent = findComponentByRouteName('CheckoutPage', routes);`
 * Then we can call static methods of given component:
 * `dispatch(PageComponent.setInitialValues({ listing, bookingDates }));`
 *
 * @param {String} nameToFind - Route name
 * @param {Array<{ route }>} routes - Route configuration as flat array.
 *
 * @return {Route} - Route that matches the given route name.
 */
export const findRouteByRouteName = (nameToFind, routes) => {
  const route = findRouteByName(nameToFind, routes);
  if (!route) {
    throw new Error(`Component "${nameToFind}" was not found.`);
  }
  return route;
};
/**
 * Get the canonical URL from the given location
 *
 * @param {Array<{ route }>} routes - Route configuration as flat array
 * @param {Object} location - location object from React Router
 *
 * @return {String} Canonical URL of the given location
 *
 */
export const canonicalRoutePath = (routes, location, pathOnly = false) => {
  const { pathname, search, hash } = location;
  const matches = matchPathname(pathname, routes);

  // Check for both ListingPage and ListingPageVariant routes
  const isListingRoute = matches.length === 1 &&
    (matches[0].route.name === 'ListingPage' || matches[0].route.name === 'ListingPageVariant');

  if (isListingRoute) {
    // Strip trailing slash and edit/draft/pending-approval variant suffix only.
    // Return the full clean path as a self-referencing canonical — no truncation.
    const cleanedPathName = pathname.replace(/\/$/, '').replace(/\/(draft|edit|pending-approval)$/, '');
    return pathOnly ? cleanedPathName : `${cleanedPathName}${search}${hash}`;
  }
  return pathOnly ? pathname : `${pathname}${search}${hash}`;
};

const LISTING_ROUTE_NAMES = new Set([
  'ListingPage',
  'ListingPageCity',
  'ListingPageCityCategory',
  'ListingPageCategory',
]);

const STORE_PROFILE_ROUTE_NAMES = new Set([
  'ProfilePage',
  'ProfilePageNoCity',
  'ProfilePageAbout',
  'ProfilePageNoCityAbout',
  'ProfilePageReviews',
  'ProfilePageNoCityReviews',
  'ProfilePageGallery',
  'ProfilePageNoCityGallery',
  'ProfilePageFaqs',
  'ProfilePageNoCityFaqs',
  'ProfilePageBlog',
  'ProfilePageNoCityBlog',
]);

const getAvailableLocales = (routeName, listing, pageAssetsData) => {
  if (LISTING_ROUTE_NAMES.has(routeName) && listing?.attributes) {
    const publicData = listing.attributes.publicData || {};
    return SUPPORTED_LANGUAGES.filter(lang => {
      if (lang === 'en') return true;
      const localizedTitle = publicData[lang]?.title;
      return typeof localizedTitle === 'string' && localizedTitle.trim().length > 0;
    });
  }

  if (STORE_PROFILE_ROUTE_NAMES.has(routeName)) {
    const publicData = pageAssetsData?.storeProfile?.publicData || {};
    return SUPPORTED_LANGUAGES.filter(lang => {
      if (lang === 'en') return true;
      const localizedName = publicData[lang]?.storeName;
      return typeof localizedName === 'string' && localizedName.trim().length > 0;
    });
  }

  return SUPPORTED_LANGUAGES;
};

export const buildAlternateLinks = ({
  config,
  routes,
  location,
  listing = {},
  pageAssetsData = {},
}) => {
  const baseUrl = config.marketplaceRootURL;
  const currentPath = location?.pathname || '';

  const pathParts = currentPath.split('/').filter(Boolean);
  const maybeLang = pathParts[0];
  const currentLocale = SUPPORTED_LANGUAGES.includes(maybeLang) ? maybeLang : 'en';

  // Remove locale from path if present
  const strippedPath = SUPPORTED_LANGUAGES.includes(maybeLang)
    ? `/${pathParts.slice(1).join('/')}`
    : currentPath;

  let matchedRoute = null;
  let params = {};

  // Match current route to find route definition
  for (const route of routes) {
    const fallbackPath = TRANSLATED_PATHS?.[route.name]?.['en']; // fallback
    const translatedPath = getTranslatedPath(route.name, fallbackPath, currentLocale);
    const matcher = match(translatedPath, { decode: decodeURIComponent });
    const result = matcher(decodeURIComponent(strippedPath));

    if (result) {
      matchedRoute = route;
      params = result.params;
      break;
    }
  }

  // Generate alternate links
  if (!matchedRoute) {
    // English has no locale prefix; other languages prepend /{lang}.
    // x-default points to the English URL as the fallback destination.
    const links = SUPPORTED_LANGUAGES.map(lang => ({
      lang,
      href: lang === 'en' ? `${baseUrl}${strippedPath}` : `${baseUrl}/${lang}${strippedPath}`,
    }));
    const enLink = links.find(l => l.lang === 'en');
    if (enLink) links.push({ lang: 'x-default', href: enLink.href });
    return links;
  }

  const locales = getAvailableLocales(matchedRoute.name, listing, pageAssetsData);

  // Listing not loaded yet — cannot derive correct slugs for any language.
  // createSlug('') returns the truthy string 'no-slug', so without this guard every
  // alternate link would contain a literal /no-slug/ path segment during SSR.
  if (LISTING_ROUTE_NAMES.has(matchedRoute.name) && !listing?.attributes) {
    return [];
  }

  const links = locales.map(lang => {
    let translated = getTranslatedPath(matchedRoute.name, matchedRoute.path, lang);

    // Replace dynamic params in translated path
    Object.entries(params).forEach(([key, val]) => {
      if (key === 'slug' && listing?.attributes) {
        const englishTitle = listing.attributes.title || '';
        const localizedTitle = listing?.attributes?.publicData?.[lang]?.title;
        const localizedSlug = localizedTitle || englishTitle;
        const slugResult = createSlug(localizedSlug);
        // Non-ASCII scripts (e.g. Chinese) produce percent-encoded slugs via encodeURIComponent.
        // Fall back to the English slug so all hreflang URLs remain human-readable ASCII.
        const finalSlug = (slugResult === 'no-slug' || slugResult.includes('%'))
          ? createSlug(englishTitle)
          : slugResult;
        translated = translated.replace(`:${key}`, finalSlug !== 'no-slug' ? finalSlug : val);
      } else if (key === 'pageId' && pageAssetsData) {
        const translatedPageAssetName = pageAssetsData?.meta?.['pageId'];
        translated = translated.replace(`:${key}`, translatedPageAssetName || val);
      } else {
        translated = translated.replace(`:${key}`, val);
      }
    });

    // Prepend locale (except for default "en")
    const finalPath = lang === 'en' ? translated : `/${lang}${translated}`;

    return {
      lang,
      href: `${baseUrl}${finalPath}`,
    };
  });

  const enLink = links.find(l => l.lang === 'en');
  if (enLink) {
    links.push({
      lang: 'x-default',
      href: enLink.href,
    });
  }

  return links;
};
